import { format, formatDistanceToNow, differenceInYears, differenceInMonths, isValid, parseISO } from 'date-fns';
import { getAppearance } from './appearance';

/** Backend NUMERIC/decimal fields are serialized as strings on *Read responses — convert before formatting or math. */
export function num(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? undefined : n;
}

/** Format a number as Indian Rupee currency. Backend money values are decimal strings — pass through num() first if needed. */
export function formatCurrency(value: number | string | null | undefined): string {
  const n = num(value);
  if (n === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

/** Format a plain number with locale grouping. */
export function formatNumber(value: number | string | null | undefined, decimals = 0): string {
  const n = num(value);
  if (n === undefined) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

// Date style chosen on Administration -> Appearance (default "14 Sep 2026").
export function formatDate(value: string | null | undefined, pattern = getAppearance().date_format): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? parseISO(value) : value;
  if (!isValid(d)) return '—';
  return format(d, pattern);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = parseISO(value);
  if (!isValid(d)) return '—';
  return format(d, `${getAppearance().date_format}, h:mm a`);
}

export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = parseISO(value);
  if (!isValid(d)) return '—';
  return formatDistanceToNow(d, { addSuffix: true });
}

/** Returns today's date in YYYY-MM-DD, suitable as a default for date inputs. */
export function today(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/** Human age string, e.g. "2 yrs 3 mo", from a date of birth. */
export function formatAge(dob: string | null | undefined): string {
  if (!dob) return '—';
  const d = parseISO(dob);
  if (!isValid(d)) return '—';
  const now = new Date();
  const years = differenceInYears(now, d);
  const months = differenceInMonths(now, d) % 12;
  if (years <= 0 && months <= 0) return '< 1 mo';
  if (years <= 0) return `${months} mo`;
  return `${years} yr${years > 1 ? 's' : ''}${months > 0 ? ` ${months} mo` : ''}`;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : plural ?? `${singular}s`;
}

/**
 * Shortens a raw UUID FK for display in tables that don't have the joined
 * label handy (e.g. a generic CRUD table rendering an animal_id FK without a
 * reference-data lookup in scope). A later pass can resolve these to names.
 */
export function shortId(value: string | null | undefined): string {
  if (!value) return '—';
  return value.length > 8 ? `${value.slice(0, 8)}…` : value;
}
